import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Anagrafica } from '../../../_models/Anagrafica';
import { Sede } from '../../../_models/Sede';
import { CampoUtenteDef } from '../../../_models/CampoUtenteDef';
import { EntitaCampoUtente } from '../../../_models/generated/entita-campo-utente';
import { RegistryService } from '../../../_services/registry.service';
import { OrganizzazioniService } from '../../../_services/organizzazioni.service';
import { CampiUtenteService } from '../../../_services/campi-utente.service';
import { CampiUtenteFormComponent } from '../../campi-utente-form/campi-utente-form.component';
import { SaveMessage } from '../../../_helpers/save-message';
// Feature 120 (issue #3368) — propagazione delle modifiche agli ordini clienti collegati.
import {
  DiffPropagazione, SediPending, calcolaDiff, haModifichePropagabili,
} from '../../../_helpers/anagrafica-propagazione';
import { AnteprimaImpattoOrdini, PropagaAiOrdiniRequest } from '../../../_services/registry.service';
import { PropagaOrdiniModalComponent } from '../propaga-ordini-modal/propaga-ordini-modal.component';
import { AuthenticationService } from '../../../_services/authentication.service';
import { constants } from '../../../_helpers/constants';

export const emptyAnagrafica = (): Anagrafica => ({
  organizzazioneId: 0,
  id: 0,
  codice: '',
  ragioneSociale: '',
  partitaIva: '',
  codFiscale: '',
  riferimento: '',
  tipi: 0,
  nota: '',
  notaConsegna: '',
  notaAggiuntiva: '',
  dataUnixRegistrazione: 0,
  isActive: true,
  sedi: []
});

const emptySede = (orgId: number, anagraficaId: number): Sede => ({
  organizzazioneId: orgId,
  anagraficaId,
  id: 0,
  codice: '',
  nome: '',
  indirizzo: '',
  citta: '',
  provincia: '',
  cap: '',
  nazione: 'IT',
  riferimento: '',
  email: '',
  telefono: '',
  tipo: 0,
  nota: '',
  isActive: true
});

@Component({
  selector: 'app-anagrafica-create',
  templateUrl: './anagrafica-create.component.html',
  styleUrls: ['./anagrafica-create.component.scss'],
  standalone: false
})
export class AnagraficaCreateComponent implements OnInit {
  @Input() anagrafica: Anagrafica;
  @Input() isNew = true;

  loading = false;
  loadingSede = false;
  activeSede: Sede | null = null;
  activeSedeIndex: number | null = null;

  // Campi utente dinamici (entità Anagrafica).
  campiUtenteAttivi: CampoUtenteDef[] = [];
  campiUtenteStorici: CampoUtenteDef[] = [];
  @ViewChild(CampiUtenteFormComponent) private campiUtenteForm?: CampiUtenteFormComponent;

  tipiAnagrafica = [
    { label: 'Cliente', value: 1 },
    { label: 'Fornitore', value: 2 },
    { label: 'Terzista', value: 4 },
    { label: 'Vettore', value: 8 },
    { label: 'Agente', value: 16 },
    { label: 'Operatore', value: 32 }
      ];

  // Feature 120 (issue #3368) — stato di apertura della scheda, sorgente del diff proposto alla
  // conferma. Copia PROFONDA: l'anagrafica arriva shallow da anagrafiche-home, e con una copia
  // superficiale la modifica di un campo cambierebbe anche lo "stato precedente".
  private snapshot: Anagrafica;
  // Modifiche alle sedi accumulate nel modale e applicate solo alla conferma (FR-015).
  private sediPending: SediPending = { creates: [], updates: [], deletes: [] };
  // Codici campo utente definiti sull'entità OrdineVendita; `null` = definizioni non disponibili.
  private codiciCampiUtenteOdV: string[] | null = [];

  constructor(
    private registryService: RegistryService,
    private organizzazioniService: OrganizzazioniService,
    private campiUtenteService: CampiUtenteService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage,
    private authService: AuthenticationService
  ) {}

  ngOnInit() {
    if (!this.anagrafica) {
      this.anagrafica = emptyAnagrafica();
      this.anagrafica.organizzazioneId = this.organizzazioniService.organizzazioneId;
    }
    if (!this.anagrafica.sedi) {
      this.anagrafica.sedi = [];
    }
    this.snapshot = this.clona(this.anagrafica);
    if (this.anagrafica.id > 0) {
      this.loadSedi();
    }
    this.loadCampiUtente();
  }

  private clona<T>(valore: T): T {
    return JSON.parse(JSON.stringify(valore ?? null));
  }

  private async loadCampiUtente() {
    const orgId = this.organizzazioniService.organizzazioneId;
    if (!orgId) return;
    try {
      this.campiUtenteAttivi = await this.campiUtenteService.getDefinizioni(orgId, true, EntitaCampoUtente.ANAGRAFICA) ?? [];
      // In modifica: carica anche le definizioni storiche (disattivate) con valore sull'anagrafica.
      if (!this.isNew && this.anagrafica?.campiUtente) {
        const all = await this.campiUtenteService.getDefinizioni(orgId, false, EntitaCampoUtente.ANAGRAFICA) ?? [];
        const attiviCodes = new Set(this.campiUtenteAttivi.map(d => d.codice));
        this.campiUtenteStorici = all.filter(d => !attiviCodes.has(d.codice));
      }
    } catch {
      this.campiUtenteAttivi = [];
      this.campiUtenteStorici = [];
    }
    await this.loadCodiciCampiUtenteOrdineVendita(orgId);
  }

  // Feature 120 (issue #3368) — codici dei campi utente definiti sull'entità OrdineVendita: sono
  // quelli con cui si cercano gli omonimi (FR-020). `null` = definizioni non recuperabili, e in quel
  // caso il dialogo dichiara che i campi utente non sono propagabili adesso, senza bloccare gli
  // scalari (FR-021). Nota: il client cachea le definizioni per 5 minuti, quindi un campo utente
  // definito da pochi istanti può risultare non propagabile fino allo scadere del TTL: caso limite
  // noto e accettato.
  private async loadCodiciCampiUtenteOrdineVendita(orgId: number) {
    try {
      const definizioni = await this.campiUtenteService.getDefinizioni(
        orgId, true, EntitaCampoUtente.ORDINE_VENDITA);
      this.codiciCampiUtenteOdV = (definizioni ?? []).map(d => d.codice);
    } catch {
      this.codiciCampiUtenteOdV = null;
    }
  }

  loadSedi() {
    this.loadingSede = true;
    this.registryService.getSediOfAnagraficaFilteredAsync(
      this.anagrafica.organizzazioneId,
      this.anagrafica.id,
      '', 0, 1000, 'codice', true
    )
      .then(result => {
        this.anagrafica.sedi = result.result;
        // Lo snapshot delle sedi va preso QUI: loadSedi è asincrona e uno snapshot fatto in ngOnInit
        // conterrebbe un array vuoto, facendo risultare NUOVA ogni sede esistente.
        this.snapshot.sedi = this.clona(result.result);
      })
      .catch(() => this.saveMessage.messageError('Errore nel caricamento delle sedi.'))
      .finally(() => this.loadingSede = false);
  }

  hasTipo(value: number): boolean {
    return (this.anagrafica.tipi & value) === value;
  }

  toggleTipo(value: number, checked: boolean) {
    if (checked) {
      this.anagrafica.tipi |= value;
    } else {
      this.anagrafica.tipi &= ~value;
    }
  }

  async confirm() {
    // Serializza i campi utente solo al salvataggio.
    if (this.campiUtenteForm) {
      this.anagrafica.campiUtente = this.campiUtenteForm.serialize();
    }
    // Il diff va calcolato PRIMA di salvare: dopo, snapshot e stato corrente coinciderebbero.
    const diff = calcolaDiff(this.snapshot, this.anagrafica, this.sediPending, this.codiciCampiUtenteOdV);
    this.loading = true;
    try {
      // Le sedi pendenti vanno applicate PRIMA del salvataggio dell'anagrafica: UpdateAnagrafica non
      // persiste le sedi dal DTO, e la propagazione deve leggere valori di sede già aggiornati.
      await this.applicaSediPending();
      const saved = await this.salvaAnagrafica();
      await this.chiediPropagazione(diff);
      this.modalCtrl.dismiss(saved, 'confirm');
    } catch {
      this.saveMessage.messageError('Ci sono problemi con il salvataggio.');
    } finally {
      this.loading = false;
    }
  }

  private async salvaAnagrafica(): Promise<Anagrafica> {
    const saved = this.isNew
      ? await this.registryService.createAnagraficaAsync(this.anagrafica)
      : await this.registryService.updateAnagraficaAsync(this.anagrafica);
    if (this.isNew) this.saveMessage.entityCreated('anagrafica', saved);
    else this.saveMessage.entityUpdated('anagrafica', saved);
    return saved;
  }

  // Feature 120 (issue #3368) — dialogo di propagazione agli ordini clienti, DOPO il salvataggio.
  // Qualunque esito qui lascia l'anagrafica salvata: la propagazione è un'operazione a sé (FR-013).
  private async chiediPropagazione(diff: DiffPropagazione) {
    if (!this.propagazioneProponibile(diff)) return;

    const anteprima = await this.anteprimaImpatto(diff);
    // Zero righe interessate ⇒ nessun dialogo (FR-002). Con il conteggio NON disponibile il dialogo
    // si apre comunque: il degrado dell'anteprima non deve impedire una propagazione corretta (FR-012).
    if (anteprima.disponibile && (anteprima.anagrafica?.righeTotali ?? 0) === 0) return;

    const request = await this.apriDialogo(diff, anteprima);
    if (!request) return;   // "Non aggiornare": nessun ordine cambia, l'anagrafica resta salvata.
    await this.avviaPropagazione(request);
  }

  private propagazioneProponibile(diff: DiffPropagazione): boolean {
    // Il gate di ruolo replica quello server-side (FR-040). Serve nonostante la sezione Anagrafiche
    // richieda già REGISTRY, perché questo modale è aperto anche da select-anagrafica-registry nella
    // creazione di un ordine cliente, dove l'operatore può avere ODV senza REGISTRY.
    // NON si usa MenuAccessService.can('vendita-ordini-clienti') (modulo ODV-BASE-UI + ruolo ODV):
    // nasconderebbe il dialogo proprio all'operatore delle anagrafiche.
    if (!this.authService.isInRole(constants.ROLE_ANA)) return false;
    if (this.anagrafica.id <= 0) return false;   // FR-026: mai su un'anagrafica inesistente
    return haModifichePropagabili(diff);
  }

  // Gli id delle sedi ELIMINATE devono entrare nei sedeIds, altrimenti l'avviso "N righe d'ordine
  // mantengono l'indirizzo attuale" resta senza numero (FR-017).
  private async anteprimaImpatto(diff: DiffPropagazione): Promise<AnteprimaImpattoOrdini> {
    const sedeIds = diff.sedi.map(s => s.sedeId);
    try {
      return await this.registryService.anteprimaImpattoOrdiniAsync(
        this.anagrafica.organizzazioneId, this.anagrafica.id, sedeIds);
    } catch {
      return { disponibile: false, sedi: [] };
    }
  }

  private async apriDialogo(diff: DiffPropagazione, anteprima: AnteprimaImpattoOrdini) {
    const modal = await this.modalCtrl.create({
      component: PropagaOrdiniModalComponent,
      // Classe dedicata (global.scss): questo dialogo si apre sopra la scheda anagrafica e deve
      // leggersi come un livello sovrapposto — fondale più fitto, misura minore, ombra netta.
      cssClass: 'propaga-ordini-modal',
      componentProps: {
        diff, anteprima,
        orgId: this.anagrafica.organizzazioneId,
        anagraficaId: this.anagrafica.id,
      },
    });
    await modal.present();
    // onDidDismiss, NON onWillDismiss: quest'ultima si risolve quando il dialogo COMINCIA a
    // chiudersi, con l'animazione di uscita ancora in corso. Il chiamante fa poi la POST (pochi ms
    // in rete locale) e chiude questo modale con `modalCtrl.dismiss()`, che senza id chiude
    // l'overlay in CIMA alla pila: se il dialogo è ancora lì, si prende lui il dismiss e la scheda
    // anagrafica resta aperta. Con onDidDismiss la pila è già stata liberata.
    const { data, role } = await modal.onDidDismiss();
    return role === 'confirm' ? (data as PropagaAiOrdiniRequest) : null;
  }

  // FR-014 — un fallimento qui deve essere ESPLICITO: il silenzio farebbe credere all'operatore che
  // gli ordini siano allineati quando non lo sono.
  private async avviaPropagazione(request: PropagaAiOrdiniRequest) {
    try {
      await this.registryService.propagaAiOrdiniAsync(request);
      this.saveMessage.messageInfo('Aggiornamento degli ordini collegati avviato.');
    } catch (errore) {
      this.saveMessage.messageError(this.messaggioDiErrore(errore));
    }
  }

  private messaggioDiErrore(errore: unknown): string {
    const stato = (errore as { status?: number })?.status;
    if (stato === 403) {
      return 'Non hai i permessi per aggiornare gli ordini collegati. '
        + 'L\'anagrafica è stata comunque salvata.';
    }
    return 'La propagazione agli ordini NON è partita: il servizio ordini non è raggiungibile. '
      + 'L\'anagrafica è stata comunque salvata.';
  }

  cancel = () => this.modalCtrl.dismiss(false, 'cancel');

  // --- Gestione sedi ---

  addSede() {
    this.activeSede = emptySede(this.anagrafica.organizzazioneId, this.anagrafica.id);
    this.activeSedeIndex = null;
  }

  editSede(sede: Sede, index: number) {
    this.activeSede = { ...sede };
    this.activeSedeIndex = index;
  }

  // Feature 120 (issue #3368) — le modifiche alle sedi NON chiamano più le API qui: diventano
  // pendenti e vengono applicate alla conferma del modale (FR-015, SC-010). È ciò che permette di
  // calcolare il diff da propagare in un unico punto, e che rende Annulla davvero reversibile.
  saveSede() {
    const sede = this.activeSede;
    if (this.activeSedeIndex === null) {
      // Id temporaneo NEGATIVO: distingue le sedi create in sessione (che nessun ordine referenzia)
      // da quelle esistenti. Viene azzerato prima della POST.
      if (sede.id === 0) sede.id = this.prossimoIdTemporaneo();
      this.anagrafica.sedi.push(sede);
      this.registraCreazione(sede);
    } else {
      this.anagrafica.sedi[this.activeSedeIndex] = sede;
      this.registraModifica(sede);
    }
    this.activeSede = null;
    this.activeSedeIndex = null;
  }

  removeSede(index: number) {
    const sede = this.anagrafica.sedi[index];
    this.anagrafica.sedi.splice(index, 1);
    // Sede creata in questa stessa sessione: sparisce e basta, non è mai esistita sul server.
    if (sede.id <= 0) {
      this.sediPending.creates = this.sediPending.creates.filter(s => s.id !== sede.id);
      return;
    }
    this.sediPending.updates = this.sediPending.updates.filter(s => s.id !== sede.id);
    if (!this.sediPending.deletes.some(s => s.id === sede.id)) this.sediPending.deletes.push(sede);
  }

  private registraCreazione(sede: Sede) {
    this.sediPending.creates.push(sede);
  }

  // Una sede creata in sessione e poi modificata resta una CREAZIONE: non esiste ancora sul server.
  private registraModifica(sede: Sede) {
    const creata = this.sediPending.creates.findIndex(s => s.id === sede.id);
    if (creata >= 0) {
      this.sediPending.creates[creata] = sede;
      return;
    }
    const modificata = this.sediPending.updates.findIndex(s => s.id === sede.id);
    if (modificata >= 0) this.sediPending.updates[modificata] = sede;
    else this.sediPending.updates.push(sede);
  }

  private prossimoIdTemporaneo(): number {
    return Math.min(0, ...this.anagrafica.sedi.map(s => s.id)) - 1;
  }

  // Ordine deletes → updates → creates: eliminare per primo evita che un vincolo di unicità sul
  // codice sede blocchi la creazione di una sede che riusa il codice di una appena eliminata.
  private async applicaSediPending() {
    if (this.anagrafica.id <= 0) return;   // il blocco sedi non è disponibile su un'anagrafica nuova
    for (const sede of this.sediPending.deletes) {
      await this.registryService.deleteASedeAsync(sede.organizzazioneId, sede.anagraficaId, sede.id);
    }
    for (const sede of this.sediPending.updates) {
      await this.registryService.updateSedeAsync(sede);
    }
    for (const sede of this.sediPending.creates) {
      await this.creaSedePendente(sede);
    }
    this.sediPending = { creates: [], updates: [], deletes: [] };
  }

  private async creaSedePendente(sede: Sede) {
    const indice = this.anagrafica.sedi.findIndex(s => s.id === sede.id);
    const salvata = await this.registryService.createSedeAsync({ ...sede, id: 0 });
    if (indice >= 0) this.anagrafica.sedi[indice] = salvata;
  }

  cancelSede() {
    this.activeSede = null;
    this.activeSedeIndex = null;
  }
}
